// src/popup/PublishPopup.js
import React, { useEffect, useState } from "react";
import { FaTimes, FaCheck, FaCheckCircle, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

// Popup-komponent för att publicera schema fram till ett visst datum
const PublishPopup = ({ isOpen, onClose, onPublish, currentPublishedUntil }) => {
  const { currentUser, companyAccountUid } = useAuth();

  // Hämta företagskontots UID
  const accountToUse = companyAccountUid || currentUser?.uid;

  // Hjälpfunktion: gör om olika datumformat till JS Date
  const normalizeDate = (value) => {
    if (!value) return null;

    // Firestore Timestamp
    if (typeof value?.toDate === "function") {
      return value.toDate();
    }

    // Redan Date
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    // Sträng eller annat
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const initialPublishedDate = normalizeDate(currentPublishedUntil);

  // State för publiceringsdatum och UI
  const [publishUntil, setPublishUntil] = useState(initialPublishedDate);
  const [currentMonth, setCurrentMonth] = useState(initialPublishedDate || new Date());
  const [isSaving, setIsSaving] = useState(false);

  // När popupen öppnas eller currentPublishedUntil ändras:
  // sätt alltid senaste publicerade datum som valt datum
  useEffect(() => {
    if (!isOpen) return;

    const latestPublishedDate = normalizeDate(currentPublishedUntil);

    setPublishUntil(latestPublishedDate);

    if (latestPublishedDate) {
      setCurrentMonth(
        new Date(
          latestPublishedDate.getFullYear(),
          latestPublishedDate.getMonth(),
          1
        )
      );
    } else {
      const today = new Date();
      setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    }
  }, [isOpen, currentPublishedUntil]);

  // Stäng popup om den inte är öppen
  if (!isOpen) return null;

  // Hjälpfunktion för att få dagar i månaden och startdag
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startingDay = firstDay.getDay();
    // Justera så att måndag är första dagen (0 = söndag, 1 = måndag)
    startingDay = startingDay === 0 ? 7 : startingDay;
    startingDay = startingDay - 1;

    return { daysInMonth, startingDay };
  };

  // Hjälpfunktion för att jämföra datum utan tid
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;

    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Hantera klick på ett datum i kalendern
  const handleDateClick = (date) => {
    setPublishUntil(date);
  };

  // Ändra månad i kalendern
  const changeMonth = (increment) => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + increment, 1)
    );
  };

  // Månadsnamn på svenska
  const monthNames = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  // Veckodagsnamn på svenska
  const dayNames = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];

  // Rendera kalendern
  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const days = [];

    // Tomma dagar före första dagen i månaden
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} style={styles.emptyDay} />);
    }

    // Dagar i månaden
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const isToday = isSameDay(date, new Date());
      const isSelectedDate = publishUntil && isSameDay(date, publishUntil);

      let dayStyle = { ...styles.day };

      if (isSelectedDate) {
        dayStyle = { ...dayStyle, ...styles.selectedDay };
      } else if (isToday) {
        dayStyle = { ...dayStyle, ...styles.today };
      }

      days.push(
        <div
          key={d}
          style={dayStyle}
          onClick={() => handleDateClick(date)}
          onMouseEnter={(e) => {
            if (!isSelectedDate && !isToday) {
              e.currentTarget.style.backgroundColor = "#f3f4f6";
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelectedDate && !isToday) {
              e.currentTarget.style.backgroundColor = "#ffffff";
            }
          }}
        >
          {d}
        </div>
      );
    }

    return days;
  };

  // Publicera schema
  const handlePublish = async () => {
    if (!publishUntil || !accountToUse || isSaving) return;

    setIsSaving(true);

    try {
      // Spara till Firestore under företagskontot
      const publishRef = collection(db, "users", accountToUse, "publishSettings");

      // Hämta befintliga dokument för att ta bort gamla
      const existingDocsSnapshot = await getDocs(publishRef);

      // Ta bort alla befintliga publiceringsinställningar
      const deletePromises = [];
      existingDocsSnapshot.forEach((docItem) => {
        deletePromises.push(deleteDoc(docItem.ref));
      });
      await Promise.all(deletePromises);

      // Skapa ny publiceringsinställning
      await addDoc(publishRef, {
        publishedUntil: publishUntil,
        updatedAt: serverTimestamp(),
        updatedBy: accountToUse,
      });

      console.log("Publicerat fram till t.o.m:", publishUntil);

      // Behåll senaste valda/publicerade datum lokalt också
      setPublishUntil(publishUntil);
      setCurrentMonth(
        new Date(publishUntil.getFullYear(), publishUntil.getMonth(), 1)
      );

      // Anropa callback för att uppdatera state i ShiftPage
      if (onPublish) {
        onPublish(publishUntil);
      }

      // Stäng popupen
      onClose();
    } catch (error) {
      console.error("Fel vid publicering:", error);
      alert("Kunde inte publicera schemat. Försök igen.");
    } finally {
      setIsSaving(false);
    }
  };

  // Stilar för komponenten
  const styles = {
    popupOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    popupContent: {
      backgroundColor: "#ffffff",
      borderRadius: 16,
      width: "90%",
      maxWidth: 900,
      maxHeight: "90vh",
      overflowY: "auto",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "20px 24px",
      borderBottom: "1px solid #e5e7eb",
    },
    title: {
      fontSize: 18,
      fontWeight: 600,
      color: "#111827",
      margin: 0,
    },
    companyInfo: {
      fontSize: 12,
      color: "#6b7280",
      marginTop: 4,
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: 20,
      cursor: "pointer",
      color: "#6b7280",
      padding: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
      transition: "background-color 0.2s",
    },
    body: {
      padding: "24px",
      display: "flex",
      gap: 24,
      flexWrap: "wrap",
    },
    leftColumn: {
      flex: 2,
      minWidth: "280px",
    },
    rightColumn: {
      flex: 1,
      minWidth: "240px",
      display: "flex",
      flexDirection: "column",
    },
    section: {
      marginBottom: 24,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: "#374151",
      marginBottom: 12,
    },
    calendarContainer: {
      backgroundColor: "#f9fafb",
      borderRadius: 12,
      padding: 20,
      border: "1px solid #e5e7eb",
    },
    calendarHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 20,
    },
    monthTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: "#111827",
    },
    monthNavButton: {
      background: "none",
      border: "none",
      fontSize: 16,
      color: "#6b7280",
      cursor: "pointer",
      padding: "8px 12px",
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background-color 0.2s",
    },
    weekDays: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: 4,
      marginBottom: 8,
    },
    weekDay: {
      textAlign: "center",
      fontSize: 12,
      fontWeight: 500,
      color: "#6b7280",
      padding: "8px 0",
    },
    calendarGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: 4,
    },
    day: {
      aspectRatio: "1/1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      color: "#374151",
      cursor: "pointer",
      borderRadius: 8,
      transition: "all 0.2s",
      backgroundColor: "#ffffff",
      userSelect: "none",
    },
    emptyDay: {
      aspectRatio: "1/1",
    },
    today: {
      backgroundColor: "#eff6ff",
      color: "#3b82f6",
      fontWeight: 600,
    },
    selectedDay: {
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      fontWeight: 600,
    },
    summaryBox: {
      backgroundColor: "#f3f4f6",
      borderRadius: 12,
      padding: "20px",
      border: "1px solid #e5e7eb",
      marginBottom: 20,
    },
    summaryTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: "#111827",
      marginBottom: 16,
    },
    summaryRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: "1px solid #e5e7eb",
      gap: 12,
    },
    summaryLabel: {
      fontSize: 14,
      color: "#6b7280",
    },
    summaryValue: {
      fontSize: 14,
      fontWeight: 600,
      color: "#111827",
      textAlign: "right",
    },
    buttonContainer: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      marginTop: "auto",
    },
    publishButton: {
      height: 52,
      backgroundColor: "#3b82f6",
      border: "none",
      borderRadius: 10,
      fontSize: 15,
      fontWeight: 600,
      color: "#ffffff",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "all 0.2s",
      width: "100%",
    },
    cancelButton: {
      height: 52,
      backgroundColor: "#ffffff",
      border: "1px solid #d1d5db",
      borderRadius: 10,
      fontSize: 15,
      fontWeight: 600,
      color: "#374151",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "all 0.2s",
      width: "100%",
    },
  };

  // Hover-effekt för knappar
  const handleButtonHover = (e, isHover) => {
    if (isHover) {
      e.currentTarget.style.opacity = "0.85";
    } else {
      e.currentTarget.style.opacity = "1";
    }
  };

  // Hover-effekt för kalenderknappar
  const handleNavButtonHover = (e, isHover) => {
    if (isHover) {
      e.currentTarget.style.backgroundColor = "#f3f4f6";
    } else {
      e.currentTarget.style.backgroundColor = "transparent";
    }
  };

  return (
    <div style={styles.popupOverlay} onClick={onClose}>
      <div style={styles.popupContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>Publicera schema</h3>
          </div>
          <span role="status" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto", marginRight: 16, fontSize: 14, fontWeight: 600, color: initialPublishedDate ? "#15803d" : "#6b7280" }}>
            {initialPublishedDate ? "Publicerat" : "Ej publicerat"}
            <FaCheckCircle aria-hidden="true" />
          </span>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => handleButtonHover(e, true)}
            onMouseLeave={(e) => handleButtonHover(e, false)}
          >
            <FaTimes />
          </button>
        </div>

        <div style={styles.body}>
          {/* Vänsterkolumn - Kalender */}
          <div style={styles.leftColumn}>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Publicerat fram till t.o.m</div>

              <div style={styles.calendarContainer}>
                {/* Kalenderhuvud med månadsväljare */}
                <div style={styles.calendarHeader}>
                  <button
                    style={styles.monthNavButton}
                    onClick={() => changeMonth(-1)}
                    onMouseEnter={(e) => handleNavButtonHover(e, true)}
                    onMouseLeave={(e) => handleNavButtonHover(e, false)}
                  >
                    <FaChevronLeft />
                  </button>

                  <span style={styles.monthTitle}>
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </span>

                  <button
                    style={styles.monthNavButton}
                    onClick={() => changeMonth(1)}
                    onMouseEnter={(e) => handleNavButtonHover(e, true)}
                    onMouseLeave={(e) => handleNavButtonHover(e, false)}
                  >
                    <FaChevronRight />
                  </button>
                </div>

                {/* Veckodagar */}
                <div style={styles.weekDays}>
                  {dayNames.map((day) => (
                    <div key={day} style={styles.weekDay}>
                      {day}
                    </div>
                  ))}
                </div>

                {/* Kalendergrid */}
                <div style={styles.calendarGrid}>{renderCalendar()}</div>
              </div>
            </div>
          </div>

          {/* Högerkolumn - Summering och knappar */}
          <div style={styles.rightColumn}>
            <div style={styles.summaryBox}>
              <div style={styles.summaryTitle}>Summering</div>
              <div style={styles.summaryRow}>
                <span style={styles.summaryLabel}>Publicerat till och med:</span>
                <span style={styles.summaryValue}>
                  {publishUntil
                    ? publishUntil.toLocaleDateString("sv-SE")
                    : "Välj datum"}
                </span>
              </div>
            </div>

            {/* Knappar */}
            <div style={styles.buttonContainer}>
              <button
                style={{
                  ...styles.publishButton,
                  opacity: isSaving ? 0.7 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
                onClick={handlePublish}
                disabled={!publishUntil || isSaving}
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
              >
                <FaCheck /> {isSaving ? "Publicerar..." : "Publicera"}
              </button>

              <button
                style={styles.cancelButton}
                onClick={onClose}
                disabled={isSaving}
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishPopup;
